import {
  QueryClient,
  useMutation,
  useQuery,
  type QueryKey,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { z } from 'zod';
import { api, getApiTenantId, isApiError, type ApiError, type RequestOptions } from './api';

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (count, err) => (isApiError(err) ? err.isRetryable && count < 2 : count < 2),
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Tenant-scoped query keys: always start with the tenant id so switching tenants never shows
 * another tenant's cached data. Usage: `tenantKey('contacts', { q })`.
 */
export function tenantKey(...parts: unknown[]): QueryKey {
  return ['t', getApiTenantId(), ...parts];
}

/** GET + optional Zod validation, keyed per tenant. */
export function useApiQuery<S extends z.ZodType | undefined = undefined>(
  key: unknown[],
  path: string,
  opts: Omit<RequestOptions<S>, 'method' | 'body' | 'signal'> & {
    query?: RequestOptions<S>['query'];
    enabled?: boolean;
  } & { staleTime?: number; refetchInterval?: number | false } = {},
) {
  const { enabled, staleTime, refetchInterval, ...req } = opts;
  return useQuery({
    queryKey: req.noTenant ? key : tenantKey(...key),
    queryFn: ({ signal }) => api.get<S>(path, { ...req, signal }),
    enabled,
    staleTime,
    refetchInterval,
  });
}

/** Mutation helper: `useApiMutation((vars) => api.post('/contacts', vars))`. */
export function useApiMutation<TData, TVars>(
  fn: (vars: TVars) => Promise<TData>,
  options?: Omit<UseMutationOptions<TData, ApiError, TVars>, 'mutationFn'>,
) {
  return useMutation<TData, ApiError, TVars>({ mutationFn: fn, ...options });
}
